// 005 FR-024, FR-030, FR-050: Add, which becomes a − qty + stepper. It shows only the server's
// quantity; while a request runs the control is busy. Screen readers hear "Quantity of English
// Reader, 2" with Increase and Decrease buttons.
import { ActivityIndicator, StyleSheet, View } from "react-native";

import { makeStyles } from "@/theme/makeStyles";
import { useTheme } from "@/theme/useTheme";
import { Button } from "@/ui/Button";
import { PressableTarget } from "@/ui/PressableTarget";
import { Text } from "@/ui/Text";

import { SHOP_COPY } from "./copy";

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    stepper: {
      flexDirection: "row",
      alignItems: "center",
      borderRadius: t.radius.button,
      backgroundColor: t.color.accent,
      minHeight: t.size.button,
    },
    step: {
      minWidth: t.size.button,
      minHeight: t.size.button,
      alignItems: "center",
      justifyContent: "center",
    },
    qty: { minWidth: 28, alignItems: "center" },
    onAccent: { color: t.color.onAccent },
  }),
);

export function QuantityControl({
  name,
  quantity,
  pending,
  disabled,
  onChange,
  testID,
}: {
  name: string;
  quantity: number;
  pending: boolean;
  disabled: boolean;
  onChange: (next: number) => void;
  testID?: string;
}) {
  const styles = useStyles();
  const theme = useTheme();
  if (quantity === 0) {
    return (
      <Button
        variant="outline"
        pending={pending}
        disabled={disabled}
        onPress={() => onChange(1)}
        accessibilityHint={name}
        testID={testID}
      >
        {SHOP_COPY.add}
      </Button>
    );
  }
  return (
    <View
      style={styles.stepper}
      accessible={false}
      aria-label={SHOP_COPY.quantityOf(name, quantity)}
      role="group"
      testID={testID}
    >
      <PressableTarget
        accessibilityRole="button"
        accessibilityLabel={`${SHOP_COPY.decrease} ${name}`}
        disabled={disabled}
        onPress={() => onChange(quantity - 1)}
        style={styles.step}
      >
        <Text weight="bold" size="lg" style={styles.onAccent}>
          −
        </Text>
      </PressableTarget>
      <View style={styles.qty} aria-live="polite">
        {pending ? (
          <ActivityIndicator color={theme.color.onAccent} size="small" />
        ) : (
          <Text
            weight="bold"
            style={styles.onAccent}
            accessibilityLabel={SHOP_COPY.quantityOf(name, quantity)}
          >
            {String(quantity)}
          </Text>
        )}
      </View>
      <PressableTarget
        accessibilityRole="button"
        accessibilityLabel={`${SHOP_COPY.increase} ${name}`}
        disabled={disabled || quantity >= 20}
        onPress={() => onChange(quantity + 1)}
        style={styles.step}
      >
        <Text weight="bold" size="lg" style={styles.onAccent}>
          +
        </Text>
      </PressableTarget>
    </View>
  );
}
